import React, { FC, useCallback, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { FormInput } from '../../../../components/input/index';
import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { useAppMutation, useDebounce, useFormat, useLocale } from '../../../../hooks/index';
import { ApiService } from '../../../../services/index';
import { SharePlanType } from '../../../../types/pool-plans.types';
import { ChildrenFormProps } from '../plan-form.types';
import { FormSchema, stepOne as stepOneFormSchema } from '../plan-form.validation';
import { PoolCombobox } from '../pool-combobox';
import { SharePlanTypeCombobox } from '../share-plan-type-combobox';

export type StepOneProps = ChildrenFormProps;

export const StepOne: FC<StepOneProps> = ({
  handleCloseModal,
  nextFormStep,
  control,
  setFormData,
  companyId,
  formData,
  lockMode,
  setError,
  clearErrors,
  filedState,
}) => {
  const [isOpenCalendar, setOpenCalendar] = useState(false);
  const { stepOne } = useWatch<FormSchema>({ control });
  const { success } = stepOneFormSchema.safeParse(stepOne);
  const { format } = useFormat();
  const { messagesLocale } = useLocale();
  const { mutate } = useAppMutation(
    [QueryKey.SHARE_PLAN_NAME_CHECK, { name: stepOne?.name || '' }],
    {
      mutationFn: async (name: string) =>
        await ApiService.get<{ isInUse: boolean }>({
          endpoint: BackendRoute.SHARE_PLANS,
          routePath: ['check-is-name-in-use', name],
        }),
      onSuccess: ({ isInUse }) => {
        setError('stepOne.name', {
          message: `${messagesLocale['NAME_SHOULD_BE_UNIQUE']}`,
          type: 'validate',
          types: { required: true },
        });
        if (!isInUse) return clearErrors('stepOne.name');
      },
      defaultErrorHandling: false,
    },
  );

  const isValid = success && !filedState('stepOne.name').invalid;

  const { debounceCallback } = useDebounce<string>({
    callback: (name) => name && mutate(name),
    debounce: 300,
  });

  const preventInputAfterZero = useCallback((value: string): string => {
    if (value.length <= 1) return value;
    if (value.startsWith('0.') || value === '0.') return value;
    if (value.startsWith('0')) return '0';
    return value;
  }, []);

  const handleTypeChange = useCallback(
    (type: SharePlanType) => {
      const { financialDetails, type: prevType } = formData('stepOne');
      const value: FormSchema['stepOne']['financialDetails'] = {
        [prevType]: undefined,
        [type]: {
          enabled: true,
          ...financialDetails?.[type],
        },
      };
      setFormData('stepOne.financialDetails', value);
    },
    [formData, setFormData],
  );

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <FormInput
          control={control}
          name="stepOne.name"
          onChange={debounceCallback}
          placeholder={<AppFormattedMessage id="PLAN_NAME" />}
        />
        <PoolCombobox companyId={companyId} control={control} />
        <FormDatePicker
          inputValue={stepOne?.date ? format(stepOne?.date, 'dd/MM/yyyy') : ''}
          inputWrapperClassName="w-full"
          isDefaultOpenCalendar={isOpenCalendar}
          onSelect={(date) => {
            setFormData('stepOne.date', date);
            setOpenCalendar(false);
          }}
          placeholder={<AppFormattedMessage id="CREATION_DATE" />}
          value={stepOne?.date}
        />
        <SharePlanTypeCombobox control={control} onSelect={handleTypeChange} />
        {stepOne?.type === SharePlanType.GROWTH_HURDLE && (
          <>
            <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-1 py-1">
              <span className="pl-[14px] pt-2 text-sm font-[450] text-brand-700">
                {<AppFormattedMessage id="HURDLE" />}
              </span>
              <div className="flex flex-col items-center gap-3 rounded-md bg-white p-4 px-4 shadow-sm">
                <FormInput
                  control={control}
                  name="stepOne.financialDetails.growth-hurdle.value"
                  numberOnly
                  placeholder={<AppFormattedMessage id="VALUATION_REQUIRED" />}
                  regexp={preventInputAfterZero}
                  shouldFormatNumber
                  wrapperClassName="w-full"
                />
                <DropDown
                  answer={[
                    messagesLocale['HURDLE_VALUE_EXPLANATION_1'],
                    messagesLocale['HURDLE_VALUE_EXPLANATION_2'],
                  ]}
                  className="rounded-lg bg-gray-50"
                  question={messagesLocale['WHAT_IS_HURDLE_VALUE']}
                />
              </div>
            </div>
            <DropDown
              answer={[
                messagesLocale['HURDLE_PLAN_EXPLANATION_1'],
                messagesLocale['HURDLE_PLAN_EXPLANATION_2'],
              ]}
              className="rounded-lg bg-gray-50"
              question={<AppFormattedMessage id="WHAT_IS_HURDLE" />}
            />
          </>
        )}
        {stepOne?.type === SharePlanType.BSPCE && (
          <>
            <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-1 py-1">
              <span className="pl-[14px] pt-2 text-sm font-[450] text-brand-700">
                <AppFormattedMessage id="STRIKE_PRICE" />
              </span>
              <div className="flex flex-col items-center gap-3 rounded-md bg-white p-4 px-4 shadow-sm">
                <FormInput
                  control={control}
                  maxDecimalPlaces={4}
                  name="stepOne.financialDetails.bspce.value"
                  placeholder={<AppFormattedMessage id="VALUE" />}
                  regexp={preventInputAfterZero}
                  wrapperClassName="w-full"
                />
                <DropDown
                  answer={messagesLocale['STRIKE_PRICE_EXPLANATION']}
                  className="rounded-lg bg-gray-50"
                  question={<AppFormattedMessage id="WHAT_IS_STRIKE_PURCHASE_PRICE" />}
                />
              </div>
            </div>
            {/* <FormInput
              control={control}
              maxDecimalPlaces={2}
              name="stepOne.financialDetails.bspce.conversionRatio"
              placeholder={<AppFormattedMessage id="CONVERSION_RATIO_X_OPTIONAL" />}
              wrapperClassName="w-full"
            /> */}
            <div className="flex w-full flex-col divide-y-[1px] divide-gray-100 rounded-lg bg-gray-50">
              <DropDown
                answer={[
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_1'],
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_2'],
                ]}
                question={<AppFormattedMessage id="WHAT_IS_CONVERSION_RATIO" />}
              />
              <DropDown
                answer={messagesLocale['BSPCE_PLAN_EXPLANATION']}
                question={<AppFormattedMessage id="WHAT_IS_BSPCE_PLAN" />}
              />
            </div>
          </>
        )}
        {stepOne?.type === SharePlanType.STOCK_OPTIONS && (
          <>
            <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-1 py-1">
              <span className="pl-[14px] pt-2 text-sm font-[450] text-brand-700">
                {<AppFormattedMessage id="STRIKE_PRICE" />}
              </span>
              <div className="flex flex-col items-center gap-3 rounded-md bg-white p-4 px-4 shadow-sm">
                <FormInput
                  control={control}
                  maxDecimalPlaces={4}
                  name="stepOne.financialDetails.stock-options.value"
                  placeholder={<AppFormattedMessage id="VALUE" />}
                  regexp={preventInputAfterZero}
                  wrapperClassName="w-full"
                />
                <DropDown
                  answer={messagesLocale['EXERCISE_PRICE_EXPLANATION']}
                  className="rounded-lg bg-gray-50"
                  question={<AppFormattedMessage id="WHAT_IS_STRIKE_PRICE" />}
                />
              </div>
            </div>
            {/* <FormInput
              control={control}
              maxDecimalPlaces={2}
              name="stepOne.financialDetails.stock-options.conversionRatio"
              placeholder={<AppFormattedMessage id="CONVERSION_RATIO_X_OPTIONAL" />}
              wrapperClassName="w-full"
            /> */}
            <div className="flex w-full flex-col divide-y-[1px] divide-gray-100 rounded-lg bg-gray-50">
              <DropDown
                answer={[
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_1'],
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_2'],
                ]}
                question={<AppFormattedMessage id="WHAT_IS_CONVERSION_RATIO" />}
              />
              <DropDown
                answer={messagesLocale['STOCK_OPTIONS_PLAN_EXPLANATION']}
                question={<AppFormattedMessage id="WHAT_IS_STOCK_OPTIONS_PLAN" />}
              />
            </div>
          </>
        )}
        {stepOne?.type === SharePlanType.WARRANTS && (
          <>
            <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-1 py-1">
              <span className="pl-[14px] pt-2 text-sm font-[450] text-brand-700">
                <AppFormattedMessage id="WARRANT_PRICE" />
              </span>
              <div className="flex flex-col items-center gap-3 rounded-md bg-white p-4 px-4 shadow-sm">
                <FormInput
                  control={control}
                  maxDecimalPlaces={4}
                  name="stepOne.financialDetails.warrants.value"
                  placeholder={<AppFormattedMessage id="VALUE" />}
                  regexp={preventInputAfterZero}
                  wrapperClassName="w-full"
                />
                <DropDown
                  answer={[
                    messagesLocale['WARRANT_PLAN_EXPLANATION_1'],
                    messagesLocale['WARRANT_PLAN_EXPLANATION_2'],
                  ]}
                  className="rounded-lg bg-gray-50"
                  question={<AppFormattedMessage id="WHAT_IS_WARRANT_PRICE" />}
                />
              </div>
            </div>
            {/* <FormInput
              control={control}
              maxDecimalPlaces={2}
              name="stepOne.financialDetails.warrants.conversionRatio"
              placeholder={<AppFormattedMessage id="CONVERSION_RATIO_X_OPTIONAL" />}
              wrapperClassName="w-full"
            /> */}
            <div className="flex w-full flex-col divide-y-[1px] divide-gray-100 rounded-lg bg-gray-50">
              <DropDown
                answer={[
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_1'],
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_2'],
                ]}
                question={<AppFormattedMessage id="WHAT_IS_CONVERSION_RATIO" />}
              />
              <DropDown
                answer={messagesLocale['WARRANT_PLAN_INVESTORS_SIDE_EXPLANATION']}
                question={<AppFormattedMessage id="WHAT_IS_WARRANT_PLAN" />}
              />
            </div>
          </>
        )}

        {stepOne?.type === SharePlanType.SAR && (
          <>
            <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-1 py-1">
              <span className="pl-[14px] pt-2 text-sm font-[450] text-brand-700">
                <AppFormattedMessage id="SAR_BASE_PRICE" />
              </span>
              <div className="flex flex-col items-center gap-3 rounded-md bg-white p-4 px-4 shadow-sm">
                <FormInput
                  control={control}
                  maxDecimalPlaces={2}
                  name="stepOne.financialDetails.sar.value"
                  placeholder={<AppFormattedMessage id="VALUE" />}
                  regexp={preventInputAfterZero}
                  wrapperClassName="w-full"
                />
                <DropDown
                  answer={messagesLocale['SAR_BASE_PRICE_EXPLANATION']}
                  className="rounded-lg bg-gray-50"
                  question={<AppFormattedMessage id="WHAT_IS_SAR_BASE_PRICE" />}
                />
              </div>
            </div>
            <DropDown
              answer={messagesLocale['SAR_PLAN_EXPLANATION']}
              className="rounded-lg bg-gray-50"
              question={<AppFormattedMessage id="WHAT_IS_SAR_PLAN" />}
            />
          </>
        )}

        {stepOne?.type === SharePlanType.RSU && (
          <>
            {/* <FormInput
              control={control}
              maxDecimalPlaces={2}
              name="stepOne.financialDetails.rsu.conversionRatio"
              placeholder={<AppFormattedMessage id="CONVERSION_RATIO_X_OPTIONAL" />}
              wrapperClassName="w-full"
            /> */}
            <div className="flex w-full flex-col divide-y-[1px] divide-gray-100 rounded-lg bg-gray-50">
              <DropDown
                answer={[
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_1'],
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_2'],
                ]}
                question={<AppFormattedMessage id="WHAT_IS_CONVERSION_RATIO" />}
              />
              <DropDown
                answer={[
                  messagesLocale['RSU_PLAN_EXPLANATION_1'],
                  messagesLocale['RSU_PLAN_EXPLANATION_2'],
                ]}
                question={<AppFormattedMessage id="WHAT_IS_RSU_PLAN" />}
              />
            </div>
          </>
        )}

        {stepOne?.type === SharePlanType.PHANTOM && (
          <>
            {/* <FormInput
              control={control}
              maxDecimalPlaces={2}
              name="stepOne.financialDetails.phantom.conversionRatio"
              placeholder={<AppFormattedMessage id="CONVERSION_RATIO_X_OPTIONAL" />}
              wrapperClassName="w-full"
            /> */}
            <div className="flex w-full flex-col divide-y-[1px] divide-gray-100 rounded-lg bg-gray-50">
              <DropDown
                answer={[
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_1'],
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_2'],
                ]}
                question={<AppFormattedMessage id="WHAT_IS_CONVERSION_RATIO" />}
              />
              <DropDown
                answer={[
                  messagesLocale['PHANTOM_PLAN_EXPLANATION_1'],
                  messagesLocale['PHANTOM_PLAN_EXPLANATION_2'],
                ]}
                question={<AppFormattedMessage id="WHAT_IS_PHANTOM_PLAN" />}
              />
            </div>
          </>
        )}

        {stepOne?.type === SharePlanType.RSA && (
          <>
            <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-1 py-1">
              <span className="pl-[14px] pt-2 text-sm font-[450] text-brand-700">
                <AppFormattedMessage id="RSA_PURCHASE_PRICE" />
              </span>
              <div className="flex flex-col items-center gap-3 rounded-md bg-white p-4 px-4 shadow-sm">
                <FormInput
                  control={control}
                  maxDecimalPlaces={4}
                  name="stepOne.financialDetails.rsa.value"
                  placeholder={<AppFormattedMessage id="VALUE" />}
                  regexp={preventInputAfterZero}
                  wrapperClassName="w-full"
                />
                <DropDown
                  answer="The Restricted Stock Award (RSA) Purchase Price is the amount an employee must pay to acquire restricted stock at the time of grant. Unlike stock options, RSAs are purchased upfront, often at a nominal or discounted price."
                  className="rounded-lg bg-gray-50"
                  question={<AppFormattedMessage id="WHAT_IS_RSA_PURCHASE_PRICE" />}
                />
              </div>
            </div>
            {/* <FormInput
              control={control}
              maxDecimalPlaces={2}
              name="stepOne.financialDetails.rsa.conversionRatio"
              placeholder={<AppFormattedMessage id="CONVERSION_RATIO_X_OPTIONAL" />}
              wrapperClassName="w-full"
            /> */}
            <div className="flex w-full flex-col divide-y-[1px] divide-gray-100 rounded-lg bg-gray-50">
              <DropDown
                answer={[
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_1'],
                  messagesLocale['CONVERSION_RATIO_EXPLANATION_2'],
                ]}
                question={<AppFormattedMessage id="WHAT_IS_CONVERSION_RATIO" />}
              />
              <DropDown
                answer={messagesLocale['RSA_PLAN_EXPLANATION']}
                question={<AppFormattedMessage id="WHAT_IS_RSA_PLAN" />}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? <AppFormattedMessage id="UPDATE" /> : <AppFormattedMessage id="NEXT" />}
          </Button>
        </div>
      </div>
    </>
  );
};
